import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Reveal, Eyebrow } from "./Reveal";

const Card = ({ r }) => (
  <figure className="flex h-full w-[300px] shrink-0 flex-col rounded-2xl border border-amber-900/40 bg-[#181512] p-6" data-testid="review-card">
    <div className="flex gap-0.5 text-amber-400" aria-hidden="true">
      {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-amber-400" />)}
    </div>
    <blockquote className="mt-4 flex-1 font-serif-it text-lg italic leading-snug text-amber-50">« {r.text} »</blockquote>
    <figcaption className="mt-4 text-sm font-semibold text-amber-300">
      — {r.name}{r.country ? <span className="font-normal text-stone-400"> · {r.country}</span> : null}
    </figcaption>
  </figure>
);

export const Testimonials = () => {
  const [reviews, setReviews] = useState(null);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [country, setCountry] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(Array.isArray(r.data) ? r.data : [])).catch(() => setReviews([]));
  }, []);

  const send = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/reviews", { name: name.trim(), text: text.trim(), country: country.trim() || null });
      toast.success("Merci ! Votre témoignage sera publié après validation du Maître.");
      setName(""); setText(""); setCountry("");
    } catch { toast.error("Envoi impossible. Réessayez."); }
    setSending(false);
  };

  const field = "w-full rounded-xl border border-amber-900/50 bg-[#0a0908] px-4 py-3 text-sm text-stone-100 outline-none focus:border-amber-500/70";

  return (
    <section id="temoignages" className="overflow-hidden py-24 md:py-36" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <Reveal><Eyebrow>Témoignages</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-3xl leading-tight text-amber-50 sm:text-4xl lg:text-5xl">
                Ils ont retrouvé l'espoir
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <p className="text-base leading-relaxed text-stone-300">
              Avis authentiques de clients accompagnés par le Maître, publiés après vérification.
            </p>
          </Reveal>
        </div>
      </div>

      {reviews !== null && reviews.length > 0 && (
        <div className="marquee-track mt-14 flex w-max gap-5" data-testid="reviews-marquee">
          {[...reviews, ...reviews].map((r, i) => <Card key={`${r.id}-${i}`} r={r} />)}
        </div>
      )}
      {reviews !== null && reviews.length === 0 && (
        <p className="mt-14 text-center text-sm text-stone-400" data-testid="reviews-empty">
          Les premiers témoignages seront publiés ici. Soyez le premier à témoigner.
        </p>
      )}

      <Reveal delay={0.1}>
        <form onSubmit={send} className="mx-auto mt-14 max-w-xl rounded-3xl border border-amber-900/40 bg-[#181512] p-7" data-testid="review-form">
          <p className="font-serif-it text-2xl italic text-amber-100">Le Maître vous a aidé ? Racontez-le.</p>
          <input className={`${field} mt-5`} placeholder="Votre prénom" value={name} maxLength={60}
            onChange={(e) => setName(e.target.value)} data-testid="review-name-input" required />
          <input className={`${field} mt-3`} placeholder="Votre pays (ex : France, Côte d'Ivoire...)" value={country} maxLength={40}
            onChange={(e) => setCountry(e.target.value)} data-testid="review-country-input" list="country-list" />
          <datalist id="country-list">
            {["France", "Belgique", "Suisse", "Canada", "Côte d'Ivoire", "Sénégal", "Cameroun", "Bénin", "Togo", "Mali", "Guinée", "Congo", "RDC", "Gabon", "Maroc", "Algérie", "Tunisie", "Haïti", "USA", "Espagne", "Italie", "Allemagne", "Royaume-Uni"].map((c) => <option key={c} value={c} />)}
          </datalist>
          <textarea className={`${field} mt-3 min-h-24`} placeholder="Votre témoignage..." value={text} maxLength={600}
            onChange={(e) => setText(e.target.value)} data-testid="review-text-input" required />
          <button disabled={sending} className="mt-4 w-full rounded-full bg-amber-400 py-3 text-sm font-bold uppercase tracking-wider text-stone-950 disabled:opacity-50" data-testid="review-submit">
            {sending ? "Envoi..." : "Envoyer mon témoignage"}
          </button>
        </form>
      </Reveal>
    </section>
  );
};
